import { ChangeDetectorRef, Component, NgZone, ChangeDetectionStrategy } from '@angular/core';
import { ToastrService, ToastPackage } from 'ngx-toastr';
import { BaseToastComponent } from '@app/shared/toasts/base-toast.component';

export interface CopyableErrorToastData {
    title: string;
    content: string;
    detail?: string;
    detailLabel?: string;
    copyLabel?: string;
    copiedLabel?: string;
    closeLabel?: string;
}

@Component({
    selector: 'app-copyable-error-toast',
    templateUrl: './copyable-error-toast.component.html',
    styleUrls: ['./copyable-error-toast.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CopyableErrorToastComponent extends BaseToastComponent {
    data: CopyableErrorToastData;
    copied = false;

    constructor(
        protected toastrService: ToastrService,
        public toastPackage: ToastPackage,
        cdr: ChangeDetectorRef,
        ngZone: NgZone
    ) {
        super(toastrService, toastPackage, cdr, ngZone);
        this.data = this.parseMessage(this.getToastMessage());
    }

    async copyDetail(event: MouseEvent): Promise<void> {
        event.stopPropagation();
        const value = this.data.detail || this.data.content || '';
        if (!value) {
            return;
        }

        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(value);
            } else {
                this.copyWithFallback(value);
            }
            this.markCopied();
        } catch {
            this.copyWithFallback(value);
            this.markCopied();
        }
    }

    dismiss(event: MouseEvent): void {
        event.stopPropagation();
        this.toastrService.clear(this.toastPackage.toastId);
    }

    private parseMessage(message: string): CopyableErrorToastData {
        try {
            return JSON.parse(message) as CopyableErrorToastData;
        } catch {
            return {
                title: 'Error',
                content: message
            };
        }
    }

    private copyWithFallback(value: string): void {
        const textarea = document.createElement('textarea');
        textarea.value = value;
        textarea.setAttribute('readonly', 'true');
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
    }

    private markCopied(): void {
        this.copied = true;
        this.cdr?.markForCheck();
        setTimeout(() => {
            this.copied = false;
            this.cdr?.markForCheck();
        }, 1800);
    }
}
